import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Phone, Mail, MapPin, Check, Paintbrush, Palette, Hammer,
  Star, ArrowRight, Clock, ShieldCheck, Menu, X, ChevronsLeftRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PHONE = "202-967-7031";
const PHONE_RAW = "2029677031";
const EMAIL = "ngohamstrong2@gmail.com";

const LOGO = "https://static.prod-images.emergentagent.com/jobs/2c4495ec-a10c-4644-a9bf-8a97a2dec650/images/a1864a4513a681cbc3ee45b1760db69bb042ff1d2001577cc605e9014fc43e83.png";

const IMG = {
  hero: "https://images.unsplash.com/photo-1682888818602-b4492fadf2f8?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  painting: "https://images.unsplash.com/photo-1599083549933-838ea352c1cc?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  consulting: "https://images.pexels.com/photos/6583367/pexels-photo-6583367.jpeg?auto=compress&cs=tinysrgb&w=1200",
  g1: "https://images.unsplash.com/photo-1682888818620-94875adf5bb9?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
  g2: "https://images.pexels.com/photos/37153437/pexels-photo-37153437.jpeg?auto=compress&cs=tinysrgb&w=1000",
  g3: "https://images.pexels.com/photos/21038559/pexels-photo-21038559.jpeg?auto=compress&cs=tinysrgb&w=1000",
};

const BEFORE_AFTER = [
  {
    title: "Bethesda Galley Kitchen",
    before: "https://images.unsplash.com/photo-1612769732688-b7d111799dca?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    after: "https://images.unsplash.com/photo-1601760561441-16420502c7e0?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  },
  {
    title: "Arlington Family Kitchen",
    before: "https://images.unsplash.com/photo-1661458079058-dff4d487c7f0?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    after: "https://images.unsplash.com/photo-1565183928294-7063f23ce0f8?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  },
  {
    title: "DC Rowhouse Refresh",
    before: "https://images.unsplash.com/photo-1715249891396-653a32ff2d39?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    after: "https://images.unsplash.com/photo-1609347744403-2306e8a9ae27?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
};

const NAV = [
  { label: "Services", href: "#services" },
  { label: "Pricing", href: "#pricing" },
  { label: "Gallery", href: "#gallery" },
  { label: "Reviews", href: "#reviews" },
  { label: "Contact", href: "#contact" },
];

function Header() {
  const [open, setOpen] = useState(false);
  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-50 border-b border-charcoal/10 bg-cream/80 backdrop-blur-xl"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-12">
        <a href="#top" data-testid="logo-link" className="flex items-center gap-2.5">
          <img src={LOGO} alt="DMV BloomCraft Home logo" className="h-10 w-10 object-contain" />
          <span className="font-serif text-xl font-bold leading-none">
            DMV BloomCraft<span className="text-sage"> Home</span>
          </span>
        </a>

        <nav className="hidden items-center gap-8 md:flex">
          {NAV.map((n) => (
            <a
              key={n.href}
              href={n.href}
              data-testid={`nav-${n.label.toLowerCase()}`}
              className="text-sm font-semibold text-charcoal/80 transition-colors hover:text-sage"
            >
              {n.label}
            </a>
          ))}
          <a
            href="#contact"
            data-testid="header-cta"
            className="rounded-lg bg-sage px-5 py-2.5 text-sm font-semibold text-white transition-colors duration-300 hover:bg-blush hover:text-charcoal"
          >
            Free Consult
          </a>
        </nav>

        <button
          data-testid="mobile-menu-toggle"
          className="md:hidden"
          onClick={() => setOpen((o) => !o)}
          aria-label="Toggle menu"
        >
          {open ? <X /> : <Menu />}
        </button>
      </div>

      {open && (
        <div className="border-t border-charcoal/10 bg-cream px-6 py-4 md:hidden" data-testid="mobile-menu">
          <div className="flex flex-col gap-4">
            {NAV.map((n) => (
              <a
                key={n.href}
                href={n.href}
                onClick={() => setOpen(false)}
                className="font-semibold text-charcoal/80"
              >
                {n.label}
              </a>
            ))}
            <a href={`tel:${PHONE_RAW}`} className="font-semibold text-sage">
              {PHONE}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}

function Hero() {
  return (
    <section id="top" className="relative overflow-hidden">
      <div className="mx-auto grid max-w-7xl items-center gap-12 px-6 py-20 lg:grid-cols-2 lg:px-12 lg:py-28">
        <motion.div initial="hidden" animate="show" variants={fadeUp}>
          <span className="inline-flex items-center gap-2 rounded-full border border-sage/40 bg-sage/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.15em] text-sage">
            <MapPin size={14} /> Serving DC · MD · VA
          </span>
          <h1 className="mt-6 font-serif text-5xl font-bold leading-[1.05] tracking-tight md:text-6xl lg:text-7xl">
            Factory-smooth
            <span className="text-sage"> cabinet </span>
            painting.
          </h1>
          <p className="mt-6 max-w-lg text-lg leading-relaxed text-charcoal/70">
            Cabinet color consulting and flawless, brush-stroke-free finishes for
            kitchens across the DMV. Refresh your space for a fraction of replacement
            cost — starting at <strong className="text-charcoal">$1,799</strong>.
          </p>
          <div className="mt-9 flex flex-wrap items-center gap-4">
            <a
              href="#contact"
              data-testid="hero-cta-consult"
              className="inline-flex items-center gap-2 rounded-lg bg-sage px-8 py-4 text-base font-semibold text-white transition-colors duration-300 hover:bg-blush hover:text-charcoal"
            >
              Book Free Consultation <ArrowRight size={18} />
            </a>
            <a
              href={`tel:${PHONE_RAW}`}
              data-testid="hero-call"
              className="inline-flex items-center gap-2 rounded-lg border border-charcoal/20 px-8 py-4 text-base font-semibold text-charcoal transition-colors hover:border-sage hover:text-sage"
            >
              <Phone size={18} /> {PHONE}
            </a>
          </div>
          <div className="mt-10 flex flex-wrap gap-x-8 gap-y-3 text-sm font-medium text-charcoal/70">
            <span className="inline-flex items-center gap-2"><Check size={16} className="text-sage" /> Free in-home color consult</span>
            <span className="inline-flex items-center gap-2"><Check size={16} className="text-sage" /> Greenbelt, MD based</span>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="relative"
        >
          <img
            src={IMG.hero}
            alt="Freshly painted white kitchen cabinets"
            className="aspect-[4/5] w-full rounded-2xl object-cover shadow-xl"
          />
          <div className="absolute -bottom-6 -left-6 hidden rounded-xl border border-charcoal/10 bg-cream px-6 py-4 shadow-lg sm:block">
            <p className="font-serif text-3xl font-bold text-sage">500+</p>
            <p className="text-xs font-semibold uppercase tracking-wider text-charcoal/60">Cabinets transformed</p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

const SERVICES = [
  {
    icon: Palette,
    title: "Cabinet Color Consulting",
    text: "Sit down with a color expert to choose the perfect palette for your home's light, style, and resale value.",
    img: IMG.consulting,
  },
  {
    icon: Paintbrush,
    title: "Factory-Smooth Painting",
    text: "Sprayed, sanded, and sealed for a durable, glass-smooth finish with zero brush marks — like new cabinets.",
    img: IMG.painting,
  },
  {
    icon: Hammer,
    title: "Home Improvement",
    text: "Trim, doors, accent walls, and detail work to tie your whole refresh together with one trusted crew.",
    img: IMG.g1,
  },
];

function Services() {
  return (
    <section id="services" className="mx-auto max-w-7xl px-6 py-20 lg:px-12 lg:py-24">
      <motion.div
        initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.3 }} variants={fadeUp}
        className="max-w-2xl"
      >
        <p className="text-sm font-semibold uppercase tracking-[0.15em] text-sage">What we do</p>
        <h2 className="mt-3 font-serif text-3xl font-bold tracking-tight md:text-5xl">
          Beautiful results, built to last
        </h2>
      </motion.div>

      <div className="mt-14 grid gap-8 md:grid-cols-3">
        {SERVICES.map((s, i) => (
          <motion.div
            key={s.title}
            data-testid={`service-card-${i}`}
            initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }}
            variants={{ ...fadeUp, show: { ...fadeUp.show, transition: { duration: 0.6, delay: i * 0.1 } } }}
            className="group overflow-hidden rounded-2xl border border-charcoal/10 bg-white transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
          >
            <div className="aspect-[3/2] overflow-hidden">
              <img src={s.img} alt={s.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
            </div>
            <div className="p-7">
              <span className="flex h-11 w-11 items-center justify-center rounded-full bg-sage/15 text-sage">
                <s.icon size={20} />
              </span>
              <h3 className="mt-5 font-serif text-2xl font-bold">{s.title}</h3>
              <p className="mt-3 leading-relaxed text-charcoal/70">{s.text}</p>
            </div>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

const STEPS = [
  { icon: Palette, t: "Consult", d: "Free in-home color consult and detailed quote." },
  { icon: ShieldCheck, t: "Prep", d: "Clean, sand, and prime every surface properly." },
  { icon: Paintbrush, t: "Finish", d: "Spray factory-smooth coats in your chosen color." },
  { icon: Clock, t: "Reveal", d: "Reassemble, inspect, and hand back your dream kitchen." },
];

function Process() {
  return (
    <section className="bg-charcoal py-20 text-cream lg:py-24">
      <div className="mx-auto max-w-7xl px-6 lg:px-12">
        <div className="max-w-2xl">
          <p className="text-sm font-semibold uppercase tracking-[0.15em] text-blush">Our process</p>
          <h2 className="mt-3 font-serif text-3xl font-bold tracking-tight md:text-5xl text-cream">
            Simple, tidy, predictable
          </h2>
        </div>
        <div className="mt-14 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
          {STEPS.map((s, i) => (
            <motion.div
              key={s.t}
              initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.3 }}
              variants={{ ...fadeUp, show: { ...fadeUp.show, transition: { duration: 0.6, delay: i * 0.1 } } }}
              data-testid={`process-step-${i}`}
            >
              <span className="flex h-12 w-12 items-center justify-center rounded-full border border-sage/40 text-sage">
                <s.icon size={22} />
              </span>
              <p className="mt-5 text-xs font-semibold uppercase tracking-wider text-blush">Step {i + 1}</p>
              <h3 className="mt-1 font-serif text-2xl font-bold text-cream">{s.t}</h3>
              <p className="mt-2 text-sm leading-relaxed text-cream/70">{s.d}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

const PLANS = [
  {
    name: "Color Consult",
    price: "$199",
    note: "credited toward your project",
    features: ["In-home color session", "Custom palette board", "Finish & sheen guidance", "Written recommendations"],
    highlight: false,
  },
  {
    name: "Cabinet Refresh",
    price: "$1,799+",
    note: "most popular",
    features: ["Up to 15 cabinet doors", "Full prep, prime & spray", "Factory-smooth finish", "Hardware reinstall", "2-year warranty"],
    highlight: true,
  },
  {
    name: "Full Kitchen",
    price: "Custom",
    note: "tailored quote",
    features: ["Unlimited doors & drawers", "Trim & island included", "Premium durable coatings", "Color consult included", "Priority scheduling"],
    highlight: false,
  },
];

function Pricing() {
  return (
    <section id="pricing" className="mx-auto max-w-7xl px-6 py-20 lg:px-12 lg:py-24">
      <motion.div
        initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.3 }} variants={fadeUp}
        className="max-w-2xl"
      >
        <p className="text-sm font-semibold uppercase tracking-[0.15em] text-sage">Transparent pricing</p>
        <h2 className="mt-3 font-serif text-3xl font-bold tracking-tight md:text-5xl">
          Honest quotes, no surprises
        </h2>
      </motion.div>

      <div className="mt-14 grid gap-8 lg:grid-cols-3">
        {PLANS.map((p, i) => (
          <motion.div
            key={p.name}
            data-testid={`pricing-card-${i}`}
            initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }}
            variants={{ ...fadeUp, show: { ...fadeUp.show, transition: { duration: 0.6, delay: i * 0.08 } } }}
            className={`relative flex flex-col rounded-2xl border p-8 transition-all duration-300 hover:-translate-y-1 ${
              p.highlight
                ? "border-sage bg-sage/5 shadow-lg"
                : "border-charcoal/10 bg-white"
            }`}
          >
            {p.highlight && (
              <span className="absolute -top-3 left-8 rounded-full bg-sage px-3 py-1 text-xs font-semibold uppercase tracking-wider text-white">
                Most Popular
              </span>
            )}
            <h3 className="font-serif text-2xl font-bold">{p.name}</h3>
            <div className="mt-4 flex items-baseline gap-2">
              <span className="font-serif text-5xl font-bold text-charcoal">{p.price}</span>
            </div>
            <p className="mt-1 text-sm capitalize text-charcoal/50">{p.note}</p>
            <ul className="mt-7 flex-1 space-y-3">
              {p.features.map((f) => (
                <li key={f} className="flex items-start gap-3 text-sm text-charcoal/80">
                  <Check size={18} className="mt-0.5 shrink-0 text-sage" /> {f}
                </li>
              ))}
            </ul>
            <a
              href="#contact"
              data-testid={`pricing-cta-${i}`}
              className={`mt-8 inline-flex items-center justify-center gap-2 rounded-lg px-6 py-3.5 text-sm font-semibold transition-colors duration-300 ${
                p.highlight
                  ? "bg-sage text-white hover:bg-blush hover:text-charcoal"
                  : "border border-charcoal/20 text-charcoal hover:border-sage hover:text-sage"
              }`}
            >
              Get this quote <ArrowRight size={16} />
            </a>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

function Gallery() {
  const items = [
    { src: IMG.painting, span: "lg:col-span-2 lg:row-span-2" },
    { src: IMG.g2, span: "" },
    { src: IMG.g1, span: "" },
    { src: IMG.g3, span: "lg:col-span-2" },
  ];
  return (
    <section id="gallery" className="bg-white py-20 lg:py-24">
      <div className="mx-auto max-w-7xl px-6 lg:px-12">
        <motion.div
          initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.3 }} variants={fadeUp}
          className="max-w-2xl"
        >
          <p className="text-sm font-semibold uppercase tracking-[0.15em] text-sage">Recent work</p>
          <h2 className="mt-3 font-serif text-3xl font-bold tracking-tight md:text-5xl">
            Kitchens we've reborn
          </h2>
        </motion.div>
        <div className="mt-12 grid auto-rows-[200px] grid-cols-2 gap-4 lg:grid-cols-4 lg:auto-rows-[220px]">
          {items.map((it, i) => (
            <motion.div
              key={i}
              data-testid={`gallery-item-${i}`}
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.6, delay: i * 0.08 }}
              className={`group overflow-hidden rounded-2xl ${it.span}`}
            >
              <img
                src={it.src}
                alt={`Painted cabinet project ${i + 1}`}
                className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function BeforeAfterSlider({ before, after, title }) {
  const [pos, setPos] = useState(50);
  const ref = useRef(null);
  const dragging = useRef(false);

  const move = (clientX) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const pct = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.max(0, Math.min(100, pct)));
  };

  const onDown = () => { dragging.current = true; };
  const onUp = () => { dragging.current = false; };
  const onMove = (e) => {
    if (!dragging.current) return;
    const x = e.touches ? e.touches[0].clientX : e.clientX;
    move(x);
  };

  useEffect(() => {
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchend", onUp);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("touchmove", onMove);
    return () => {
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchend", onUp);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("touchmove", onMove);
    };
  }, []);

  return (
    <div
      ref={ref}
      data-testid={`ba-slider-${title.replace(/\s+/g, "-").toLowerCase()}`}
      className="relative aspect-[4/3] w-full select-none overflow-hidden rounded-2xl border border-charcoal/10"
      onMouseDown={(e) => { onDown(); move(e.clientX); }}
      onTouchStart={(e) => { onDown(); move(e.touches[0].clientX); }}
    >
      <img src={after} alt={`${title} after`} className="absolute inset-0 h-full w-full object-cover" draggable={false} />
      <span className="absolute right-4 top-4 rounded-full bg-sage px-3 py-1 text-xs font-semibold text-white">After</span>

      <div className="absolute inset-0 overflow-hidden" style={{ width: `${pos}%` }}>
        <img
          src={before} alt={`${title} before`}
          className="absolute inset-0 h-full w-full max-w-none object-cover"
          style={{ width: ref.current ? ref.current.getBoundingClientRect().width : "100%" }}
          draggable={false}
        />
        <span className="absolute left-4 top-4 rounded-full bg-charcoal px-3 py-1 text-xs font-semibold text-cream">Before</span>
      </div>

      <div className="absolute top-0 bottom-0 z-10 w-0.5 bg-white" style={{ left: `${pos}%` }}>
        <div className="absolute top-1/2 left-1/2 flex h-10 w-10 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white shadow-lg">
          <ChevronsLeftRight size={18} className="text-charcoal" />
        </div>
      </div>
    </div>
  );
}

function Transformations() {
  const [idx, setIdx] = useState(0);
  const item = BEFORE_AFTER[idx];
  return (
    <section id="transformations" className="bg-white py-20 lg:py-24">
      <div className="mx-auto max-w-5xl px-6 lg:px-12">
        <motion.div
          initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.3 }} variants={fadeUp}
          className="max-w-2xl"
        >
          <p className="text-sm font-semibold uppercase tracking-[0.15em] text-sage">Drag to reveal</p>
          <h2 className="mt-3 font-serif text-3xl font-bold tracking-tight md:text-5xl">
            Before &amp; after
          </h2>
          <p className="mt-4 leading-relaxed text-charcoal/70">
            Slide the handle to see the BloomCraft difference — same cabinets, brand-new look.
          </p>
        </motion.div>

        <motion.div
          key={idx}
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mt-10"
        >
          <BeforeAfterSlider {...item} />
        </motion.div>

        <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
          <p className="font-serif text-xl font-bold" data-testid="ba-title">{item.title}</p>
          <div className="flex gap-2">
            {BEFORE_AFTER.map((b, i) => (
              <button
                key={i}
                data-testid={`ba-tab-${i}`}
                onClick={() => setIdx(i)}
                className={`rounded-lg px-4 py-2 text-sm font-semibold transition-colors ${
                  i === idx ? "bg-sage text-white" : "border border-charcoal/15 text-charcoal/70 hover:border-sage hover:text-sage"
                }`}
              >
                {i + 1}
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

const REVIEWS = [
  { name: "Marcia T.", city: "Bethesda, MD", text: "Our oak cabinets look brand new — smooth as glass. The color consult alone was worth it." },
  { name: "Devon & Amara", city: "Arlington, VA", text: "Tidy, on-time, and the finish is flawless. Saved us thousands versus a full remodel." },
  { name: "Grace L.", city: "Washington, DC", text: "BloomCraft made the whole process easy. The sage green they recommended is perfect." },
];

function Reviews() {
  return (
    <section id="reviews" className="mx-auto max-w-7xl px-6 py-20 lg:px-12 lg:py-24">
      <motion.div
        initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.3 }} variants={fadeUp}
        className="max-w-2xl"
      >
        <p className="text-sm font-semibold uppercase tracking-[0.15em] text-sage">Happy homeowners</p>
        <h2 className="mt-3 font-serif text-3xl font-bold tracking-tight md:text-5xl">
          Loved across the DMV
        </h2>
      </motion.div>
      <div className="mt-12 grid gap-8 md:grid-cols-3">
        {REVIEWS.map((r, i) => (
          <motion.div
            key={r.name}
            data-testid={`review-card-${i}`}
            initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }}
            variants={{ ...fadeUp, show: { ...fadeUp.show, transition: { duration: 0.6, delay: i * 0.1 } } }}
            className="rounded-2xl border border-charcoal/10 bg-white p-7"
          >
            <div className="flex gap-1 text-blush">
              {Array.from({ length: 5 }).map((_, k) => (
                <Star key={k} size={18} fill="currentColor" />
              ))}
            </div>
            <p className="mt-5 leading-relaxed text-charcoal/80">"{r.text}"</p>
            <div className="mt-6">
              <p className="font-serif text-lg font-bold">{r.name}</p>
              <p className="text-sm text-charcoal/50">{r.city}</p>
            </div>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

const SERVICE_OPTIONS = [
  "Cabinet Color Consulting",
  "Factory-Smooth Cabinet Painting",
  "Full Kitchen Refresh",
  "Home Improvement",
  "Not sure yet",
];

function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "", message: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please fill in your name, email, and phone.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, form);
      toast.success("Thank you! We'll reach out within one business day.");
      setForm({ name: "", email: "", phone: "", service: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please call us at " + PHONE);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="bg-charcoal py-20 text-cream lg:py-24">
      <div className="mx-auto grid max-w-7xl gap-14 px-6 lg:grid-cols-2 lg:px-12">
        <div>
          <p className="text-sm font-semibold uppercase tracking-[0.15em] text-blush">Free consultation</p>
          <h2 className="mt-3 font-serif text-3xl font-bold tracking-tight text-cream md:text-5xl">
            Let's bring your kitchen back to life
          </h2>
          <p className="mt-5 max-w-md leading-relaxed text-cream/70">
            Tell us a little about your project and we'll schedule your free in-home
            color consultation and quote.
          </p>

          <div className="mt-10 space-y-5">
            <a href={`tel:${PHONE_RAW}`} data-testid="contact-phone" className="flex items-center gap-4 text-cream/90 transition-colors hover:text-sage">
              <span className="flex h-11 w-11 items-center justify-center rounded-full bg-sage/20 text-sage"><Phone size={18} /></span>
              <span className="font-semibold">{PHONE}</span>
            </a>
            <a href={`mailto:${EMAIL}`} data-testid="contact-email" className="flex items-center gap-4 text-cream/90 transition-colors hover:text-sage">
              <span className="flex h-11 w-11 items-center justify-center rounded-full bg-sage/20 text-sage"><Mail size={18} /></span>
              <span className="font-semibold">{EMAIL}</span>
            </a>
            <div className="flex items-center gap-4 text-cream/90">
              <span className="flex h-11 w-11 items-center justify-center rounded-full bg-sage/20 text-sage"><MapPin size={18} /></span>
              <span className="font-semibold">Greenbelt, MD · Serving DC, MD & VA</span>
            </div>
          </div>
        </div>

        <form
          data-testid="consult-form"
          onSubmit={submit}
          className="rounded-2xl border border-cream/15 bg-cream/5 p-7 backdrop-blur-sm lg:p-9"
        >
          <div className="grid gap-5">
            <div>
              <label className="mb-2 block text-sm font-semibold text-cream/80">Full name *</label>
              <Input
                data-testid="input-name" value={form.name} onChange={set("name")}
                placeholder="Jane Doe"
                className="border-cream/20 bg-cream/10 text-cream placeholder:text-cream/40 focus-visible:ring-sage"
              />
            </div>
            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <label className="mb-2 block text-sm font-semibold text-cream/80">Email *</label>
                <Input
                  data-testid="input-email" type="email" value={form.email} onChange={set("email")}
                  placeholder="jane@email.com"
                  className="border-cream/20 bg-cream/10 text-cream placeholder:text-cream/40 focus-visible:ring-sage"
                />
              </div>
              <div>
                <label className="mb-2 block text-sm font-semibold text-cream/80">Phone *</label>
                <Input
                  data-testid="input-phone" value={form.phone} onChange={set("phone")}
                  placeholder="(202) 000-0000"
                  className="border-cream/20 bg-cream/10 text-cream placeholder:text-cream/40 focus-visible:ring-sage"
                />
              </div>
            </div>
            <div>
              <label className="mb-2 block text-sm font-semibold text-cream/80">Service interested in</label>
              <Select value={form.service} onValueChange={(v) => setForm((f) => ({ ...f, service: v }))}>
                <SelectTrigger data-testid="select-service" className="border-cream/20 bg-cream/10 text-cream focus:ring-sage">
                  <SelectValue placeholder="Choose a service" />
                </SelectTrigger>
                <SelectContent>
                  {SERVICE_OPTIONS.map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="mb-2 block text-sm font-semibold text-cream/80">Project details</label>
              <Textarea
                data-testid="input-message" value={form.message} onChange={set("message")}
                rows={4} placeholder="Tell us about your kitchen, timeline, and color ideas..."
                className="border-cream/20 bg-cream/10 text-cream placeholder:text-cream/40 focus-visible:ring-sage"
              />
            </div>
            <Button
              data-testid="submit-consult"
              type="submit" disabled={loading}
              className="h-12 w-full bg-sage text-base font-semibold text-white transition-colors duration-300 hover:bg-blush hover:text-charcoal"
            >
              {loading ? "Sending..." : "Request Free Consultation"}
            </Button>
          </div>
        </form>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="border-t border-charcoal/10 bg-cream py-12">
      <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-6 px-6 lg:flex-row lg:px-12">
        <div className="flex items-center gap-2.5">
          <img src={LOGO} alt="DMV BloomCraft Home logo" className="h-9 w-9 object-contain" />
          <span className="font-serif text-lg font-bold">DMV BloomCraft Home</span>
        </div>
        <p className="text-sm text-charcoal/60">
          © {new Date().getFullYear()} DMV BloomCraft Home · Greenbelt, MD · {PHONE}
        </p>
        <a href="#contact" className="text-sm font-semibold text-sage hover:text-charcoal">
          Free Consultation →
        </a>
      </div>
    </footer>
  );
}

export default function Landing() {
  return (
    <div className="bg-cream">
      <Header />
      <Hero />
      <Services />
      <Process />
      <Pricing />
      <Gallery />
      <Transformations />
      <Reviews />
      <Contact />
      <Footer />
    </div>
  );
}
